import { alpha, createTheme } from '@mui/material';

// IMS design tokens (light theme). Source of truth: the IMS design system artifact.
export const tokens = {
    bg: '#f5f7f9',
    surface: '#ffffff',
    surfaceSunken: '#eef2f5',
    surfaceHover: '#e6f1f7',
    stripe: '#eef4f8',
    // Column headings on every table: a clear blue tint with dark blue titles
    tableHead: '#cfe1ec',
    border: '#dde4ea',
    borderControl: '#7f909c',
    ink: '#0f2230',
    inkMuted: '#4c6070',
    inkSubtle: '#62717d',
    brand: '#00547c',
    brandHover: '#00405f',
    brandSubtle: '#e6f1f7',
    accent: '#159cd1',
    navy: '#002a3e',
    navBg: '#ffffff',
    navInk: '#34495a',
    navInkMuted: '#62717d',
    navActive: '#e6f1f7',
    navActiveInk: '#00547c',
    navHover: '#f0f6fa',
    navDivider: '#dde4ea',
    success: '#17663f',
    successSubtle: '#e3f3ea',
    warning: '#7a4f00',
    warningSubtle: '#fcefd2',
    danger: '#a8231a',
    dangerSubtle: '#fde7e5',
    dangerFill: '#b42318',
    neutral: '#3d5a6c',
    neutralSubtle: '#e8eef2',
    chart: ['#1a7fc1', '#d9741c', '#7a5fd0', '#1f9e8a']
};

const fontSans = 'Inter, "Segoe UI", system-ui, sans-serif';
export const fontMono = '"JetBrains Mono", ui-monospace, Menlo, monospace';

export const theme = createTheme({
    palette: {
        primary: {
            main: tokens.brand,
            dark: tokens.brandHover,
            contrastText: '#ffffff'
        },
        secondary: {
            main: tokens.accent
        },
        success: { main: tokens.success, light: tokens.successSubtle },
        warning: { main: tokens.warning, light: tokens.warningSubtle },
        error: { main: tokens.dangerFill, light: tokens.dangerSubtle },
        info: { main: tokens.neutral, light: tokens.neutralSubtle },
        background: {
            default: tokens.bg,
            paper: tokens.surface
        },
        text: {
            primary: tokens.ink,
            secondary: tokens.inkMuted,
            disabled: alpha(tokens.ink, 0.38)
        },
        divider: tokens.border,
        accent: {
            main: tokens.brand,
            dark: tokens.navy
        },
        action: {
            hover: tokens.surfaceHover,
            selected: tokens.brandSubtle,
            focus: alpha(tokens.accent, 0.24)
        },
        categoryColors: tokens.chart
    },
    shape: {
        borderRadius: 6
    },
    typography: {
        fontFamily: fontSans,
        fontSize: 14,
        h4: { fontWeight: 600, fontSize: '1.5rem', lineHeight: 1.25 },
        h5: { fontWeight: 600 },
        h6: { fontWeight: 600, fontSize: '1rem' },
        button: { textTransform: 'none', fontWeight: 600 },
        caption: {}
    },
    components: {
        MuiCssBaseline: {
            styleOverrides: { body: { backgroundColor: tokens.bg } }
        },
        MuiButton: {
            defaultProps: { disableElevation: true },
            styleOverrides: {
                root: { borderRadius: 6 },
                outlined: {
                    borderColor: tokens.borderControl,
                    color: tokens.ink,
                    '&:hover': { borderColor: tokens.borderControl, backgroundColor: tokens.surfaceHover }
                }
            }
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    backgroundColor: tokens.surface,
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: tokens.borderControl },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: tokens.brand, borderWidth: 1 },
                    '&.Mui-focused': { boxShadow: `0 0 0 3px ${tokens.brandSubtle}` }
                }
            }
        },
        MuiPaper: {
            styleOverrides: {
                rounded: { borderRadius: 10 },
                elevation1: { boxShadow: '0 1px 2px rgba(15,34,48,0.06)', border: `1px solid ${tokens.border}` }
            }
        },
        MuiCard: {
            defaultProps: { elevation: 0 },
            styleOverrides: {
                root: { borderRadius: 10, border: `1px solid ${tokens.border}`, boxShadow: '0 1px 2px rgba(15,34,48,0.06)' }
            }
        },
        MuiDialog: {
            styleOverrides: {
                paper: { borderRadius: 10, boxShadow: '0 8px 24px rgba(15,34,48,0.14)' }
            }
        },
        MuiDialogTitle: {
            styleOverrides: { root: { fontWeight: 600, fontSize: '1.125rem' } }
        },
        MuiDialogActions: {
            styleOverrides: {
                root: { padding: '16px 24px', backgroundColor: tokens.surfaceSunken, borderTop: `1px solid ${tokens.border}` }
            }
        },
        MuiTableCell: {
            styleOverrides: {
                root: { borderBottom: `1px solid ${tokens.border}` },
                head: {
                    backgroundColor: tokens.tableHead,
                    color: tokens.brandHover,
                    fontSize: 11,
                    fontWeight: 600,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    lineHeight: '16px'
                }
            }
        },
        MuiTableRow: {
            styleOverrides: {
                root: { '&.MuiTableRow-hover:hover': { backgroundColor: tokens.surfaceHover } }
            }
        },
        // Alternating rows on every table. On the row, so a row that sets its own colour (a selected
        // row, a highlight) keeps it
        MuiTableBody: {
            styleOverrides: {
                root: {
                    '& > tr': { backgroundColor: tokens.surface },
                    '& > tr:nth-of-type(even)': { backgroundColor: tokens.stripe },
                    '& > tr.MuiTableRow-hover:hover': { backgroundColor: tokens.surfaceHover }
                }
            }
        },
        // Messages in strong ink on a soft fill. MUI would derive the text from the pale "light"
        // colours above, which left error text barely readable
        MuiAlert: {
            styleOverrides: {
                standardError: { color: tokens.danger, backgroundColor: tokens.dangerSubtle, '& .MuiAlert-icon': { color: tokens.danger } },
                standardWarning: { color: tokens.warning, backgroundColor: tokens.warningSubtle, '& .MuiAlert-icon': { color: tokens.warning } },
                standardSuccess: { color: tokens.success, backgroundColor: tokens.successSubtle, '& .MuiAlert-icon': { color: tokens.success } },
                standardInfo: { color: tokens.neutral, backgroundColor: tokens.neutralSubtle, '& .MuiAlert-icon': { color: tokens.neutral } },
                message: { fontWeight: 500 }
            }
        },
        MuiChip: {
            styleOverrides: {
                root: { borderRadius: 999, fontWeight: 500 }
            }
        }
    }
});
